import { handleFileUpload } from '@backmarket/front-upload'
import type { UploadEntry } from '@backmarket/front-upload/dist/types/types'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { getHttpBaseUrl } from '@backmarket/nuxt-module-http/getHttpBaseUrl'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

export class UploadFailedError extends Error {
  constructor(message = 'Upload failed') {
    super(message)
    this.name = 'UploadFailedError'
  }
}
async function uploadAttachment(
  businessRule: string,
  serviceContext: Record<string, unknown>,
  file: UploadEntry | File,
) {
  const uploadStartTime = Date.now()

  const logger = useLogger()

  logger.info(`${businessRule} upload: Start`, {
    step: `upload-${businessRule}-start`,
    subService: 'front-upload',
    timestamp: uploadStartTime,
    uploadFile: file,
    owners: ['bot-squad-sales-customer-care-front'],
  })

  let uploadedFile
  try {
    uploadedFile = await handleFileUpload(
      `${getHttpBaseUrl()}/services/uploads`,
      businessRule,
      serviceContext,
      file,
    )
  } catch (error) {
    throw new UploadFailedError()
  }

  switch (uploadedFile.status) {
    case 'success': {
      const endTime = Date.now()

      logger.info(`${businessRule} upload: Done`, {
        elapsedTime: endTime - uploadStartTime,
        step: `upload-${businessRule}-done`,
        subService: 'front-upload',
        timestamp: endTime,
        uploadFile: file,
        uploadEntry: uploadedFile,
        owners: ['bot-squad-sales-customer-care-front'],
      })

      return uploadedFile.handle
    }

    case 'failed':
    default:
      throw new UploadFailedError()
  }
}

export const useUploadAttachments = () => {
  const { user, fetchUser } = useUserStore()

  const uploadAndGetAttachmentsIds = async ({
    attachments,
    customerId,
  }: {
    attachments?: File[] | null
    customerId: number | undefined
  }) => {
    await fetchUser()

    const fetchedClientId = user.value.clientId

    if (customerId !== fetchedClientId) {
      return []
    }

    const attachmentsUploadPromises = attachments?.map((file) =>
      uploadAttachment(
        'care_resolution_attachment',
        { customerId: customerId?.toString() },
        file,
      ),
    )

    if (attachmentsUploadPromises) {
      return await Promise.all(attachmentsUploadPromises)
    }

    return []
  }

  return { uploadAndGetAttachmentsIds }
}
